import { useCallback, useEffect, useMemo, useState } from 'react';
import { format, startOfDay, subDays, subMonths, startOfMonth, endOfMonth, startOfWeek, endOfWeek, isSameDay } from 'date-fns';
import { es } from 'date-fns/locale';
import {
  Box,
  BlockStack,
  Button,
  DatePicker,
  InlineGrid,
  InlineStack,
  OptionList,
  Popover,
  Scrollable,
  Text,
  TextField,
} from '@/lib/polaris';
import { CalendarIcon } from '@/lib/polaris-icons';

export type PolarisDateRangeValue = { start: Date; end: Date };

type PresetValue =
  | 'today'
  | 'yesterday'
  | 'last7days'
  | 'last30days'
  | 'last90days'
  | 'last365days'
  | 'last12months'
  | 'lastWeek'
  | 'lastMonth'
  | 'custom';

const PRESETS: Array<{ value: PresetValue; label: string; shortLabel: string }> = [
  { value: 'today', label: 'Hoy', shortLabel: 'Hoy' },
  { value: 'yesterday', label: 'Ayer', shortLabel: 'Ayer' },
  { value: 'last7days', label: 'Últimos siete días', shortLabel: '7 días' },
  { value: 'last30days', label: 'Últimos 30 días', shortLabel: '30 días' },
  { value: 'last90days', label: 'Últimos 90 días', shortLabel: '90 días' },
  { value: 'last365days', label: 'Últimos 365 días', shortLabel: '365 días' },
  { value: 'last12months', label: 'Últimos 12 meses', shortLabel: '12 meses' },
  { value: 'lastWeek', label: 'Semana pasada', shortLabel: 'Semana pasada' },
  { value: 'lastMonth', label: 'Mes pasado', shortLabel: 'Mes pasado' },
  { value: 'custom', label: 'Personalizado', shortLabel: 'Personalizado' },
];

function getPresetRange(preset: PresetValue): PolarisDateRangeValue {
  const today = startOfDay(new Date());

  switch (preset) {
    case 'today':
      return { start: today, end: today };
    case 'yesterday': {
      const y = subDays(today, 1);
      return { start: y, end: y };
    }
    case 'last7days': {
      const start = subDays(today, 6);
      return { start, end: today };
    }
    case 'last30days': {
      const start = subDays(today, 29);
      return { start, end: today };
    }
    case 'last90days': {
      const start = subDays(today, 89);
      return { start, end: today };
    }
    case 'last365days': {
      const start = subDays(today, 364);
      return { start, end: today };
    }
    case 'last12months': {
      const start = subMonths(today, 12);
      return { start, end: today };
    }
    case 'lastWeek': {
      const lastWeekEnd = endOfWeek(subDays(today, 7));
      const lastWeekStart = startOfWeek(subDays(today, 7));
      return { start: startOfDay(lastWeekStart), end: startOfDay(lastWeekEnd) };
    }
    case 'lastMonth': {
      const lastMonth = subMonths(today, 1);
      return { start: startOfDay(startOfMonth(lastMonth)), end: startOfDay(endOfMonth(lastMonth)) };
    }
    case 'custom':
    default:
      return { start: today, end: today };
  }
}

function sameRange(a: PolarisDateRangeValue, b: PolarisDateRangeValue) {
  return isSameDay(a.start, b.start) && isSameDay(a.end, b.end);
}

export function PolarisDateRangePopover(props: {
  value: PolarisDateRangeValue;
  onChange: (next: PolarisDateRangeValue) => void;
  initialPreset?: PresetValue;
  buttonSize?: 'slim' | 'medium' | 'large';
  /** 'button' = botón estándar, 'kpi' = cuadro estilo KPI (label + valor en 2 líneas),
   *  'tertiary' = botón terciario con icono de calendario (estilo PeriodSelector del KpiBar) */
  activatorVariant?: 'button' | 'kpi' | 'tertiary';
}) {
  const { value, onChange, initialPreset = 'custom', buttonSize = 'slim', activatorVariant = 'button' } = props;

  const [active, setActive] = useState(false);
  const [selectedPreset, setSelectedPreset] = useState<PresetValue>(initialPreset);

  const [tempDates, setTempDates] = useState<PolarisDateRangeValue>(value);
  const [isSelectingRange, setIsSelectingRange] = useState(false);

  const [datePickerMonth, setDatePickerMonth] = useState(tempDates.start.getMonth());
  const [datePickerYear, setDatePickerYear] = useState(tempDates.start.getFullYear());

  useEffect(() => {
    if (!active) return;
    setTempDates(value);
    setIsSelectingRange(false);
    setDatePickerMonth(value.start.getMonth());
    setDatePickerYear(value.start.getFullYear());
  }, [active, value]);

  // Mantener preset seleccionado si el value coincide con algún preset.
  useEffect(() => {
    for (const p of PRESETS) {
      if (p.value === 'custom') continue;
      const r = getPresetRange(p.value);
      if (sameRange(value, r)) {
        setSelectedPreset(p.value);
        return;
      }
    }
    setSelectedPreset('custom');
  }, [value]);

  const formatDateRangeDisplay = useCallback((short = false) => {
    const pattern = short ? 'dd/MM/yy' : 'dd/MM/yyyy';
    const startStr = format(value.start, pattern, { locale: es });
    if (value.end && !isSameDay(value.start, value.end)) {
      const endStr = format(value.end, pattern, { locale: es });
      return `${startStr} – ${endStr}`;
    }
    return startStr;
  }, [value.end, value.start]);

  const buttonLabel = useMemo(() => {
    const preset = PRESETS.find((p) => p.value === selectedPreset);
    if (preset && preset.value !== 'custom') return preset.shortLabel;
    // Formato corto en variantes compactas (kpi/tertiary); largo solo en el botón estándar.
    return formatDateRangeDisplay(activatorVariant !== 'button');
  }, [formatDateRangeDisplay, selectedPreset, activatorVariant]);

  const isCustomRange = useMemo(
    () => value.end && !isSameDay(value.start, value.end),
    [value.start, value.end]
  );
  const customStartStr = useMemo(
    () => format(value.start, 'dd/MM/yy', { locale: es }),
    [value.start]
  );
  const customEndStr = useMemo(
    () => (value.end ? format(value.end, 'dd/MM/yy', { locale: es }) : ''),
    [value.end]
  );

  const handleMonthChange = useCallback((month: number, year: number) => {
    setDatePickerMonth(month);
    setDatePickerYear(year);
  }, []);

  const handleDateChange = useCallback((range: { start: Date; end: Date } | Date) => {
    if (range instanceof Date) {
      const date = startOfDay(range);
      setTempDates({ start: date, end: date });
      setSelectedPreset('custom');
    } else {
      const start = startOfDay(range.start);
      const end = range.end ? startOfDay(range.end) : start;
      setTempDates({ start, end });
      setSelectedPreset('custom');
    }
    setIsSelectingRange(true);
  }, []);

  const handlePresetSelect = useCallback((presetValue: PresetValue) => {
    const next = getPresetRange(presetValue);
    setTempDates(next);
    setSelectedPreset(presetValue);
    setIsSelectingRange(false);
    setDatePickerMonth(next.start.getMonth());
    setDatePickerYear(next.start.getFullYear());
  }, []);

  const handleApply = useCallback(() => {
    onChange(tempDates);
    setActive(false);
    setIsSelectingRange(false);
  }, [onChange, tempDates]);

  const openPopover = useCallback(() => {
    setTempDates(value);
    setIsSelectingRange(false);
    setActive(true);
  }, [value]);

  return (
    <Popover
      active={active}
      fluidContent
      preferredAlignment="left"
      preferredPosition="below"
      activator={
        activatorVariant === 'kpi' ? (
          <Box
            as="button"
            type="button"
            minWidth="180px"
            padding="300"
            background="bg-surface"
            borderRadius="100"
            onClick={openPopover}
            aria-label={`Fecha seleccionada: ${String(buttonLabel ?? '')}`}
            style={{ border: 'none', cursor: 'pointer', textAlign: 'left' }}
          >
            <BlockStack gap="100">
              <Text variant="bodySm" tone="subdued" as="span">
                Fecha
              </Text>
              {activatorVariant === 'kpi' && selectedPreset === 'custom' && isCustomRange ? (
                <BlockStack gap="050">
                  <Text variant="headingLg" as="span">
                    {customStartStr}
                  </Text>
                  <Text variant="bodyMd" tone="subdued" as="span">
                    al {customEndStr}
                  </Text>
                </BlockStack>
              ) : (
                <Text variant="headingLg" as="span">
                  {buttonLabel}
                </Text>
              )}
            </BlockStack>
          </Box>
        ) : activatorVariant === 'tertiary' ? (
          // Estilo reciclado del PeriodSelector del KpiBar: botón terciario + icono calendario.
          <Button variant="tertiary" icon={CalendarIcon} onClick={openPopover}>
            {buttonLabel}
          </Button>
        ) : (
          <Button
            variant="secondary"
            size={buttonSize}
            icon={CalendarIcon}
            onClick={openPopover}
          >
            {buttonLabel}
          </Button>
        )
      }
      onClose={() => {
        setActive(false);
        setTempDates(value);
        setIsSelectingRange(false);
      }}
      autofocusTarget="first-node"
    >
      <Box>
        {/* Anchos ACOTADOS (presets 180px + calendario 540px ≈ 720px). El DatePicker
            de Polaris es responsivo: si el contenedor no tiene tope, se estira y queda
            gigante. Con columnas fijas compactas se ve como el de Shopify. */}
        <InlineGrid columns={{ xs: 1, md: '180px 540px' }} gap="0">
          {/* Columna izquierda: presets (estilo Shopify, compacto). */}
          <Box borderInlineEndWidth="025" borderColor="border" padding="200">
            <div style={{ maxHeight: '360px' }}>
              <Scrollable>
                <OptionList
                  options={PRESETS.map((p) => ({ value: p.value, label: p.label }))}
                  selected={[selectedPreset]}
                  onChange={(selected) => {
                    if (selected.length > 0) handlePresetSelect(selected[0] as PresetValue);
                  }}
                />
              </Scrollable>
            </div>
          </Box>

          {/* Columna derecha: inputs de fecha + dos calendarios + acciones. */}
          <Box padding="300">
            <BlockStack gap="300">
              <InlineGrid columns="1fr auto 1fr" gap="200" alignItems="center">
                <TextField
                  label="Fecha inicial"
                  labelHidden
                  readOnly
                  value={format(tempDates.start, "d 'de' MMMM 'de' yyyy", { locale: es })}
                  autoComplete="off"
                  onChange={() => {}}
                />
                <Text variant="bodyMd" tone="subdued" as="span">
                  →
                </Text>
                <TextField
                  label="Fecha final"
                  labelHidden
                  readOnly
                  value={format(tempDates.end || tempDates.start, "d 'de' MMMM 'de' yyyy", { locale: es })}
                  autoComplete="off"
                  onChange={() => {}}
                />
              </InlineGrid>

              <DatePicker
                month={datePickerMonth}
                year={datePickerYear}
                selected={tempDates}
                onMonthChange={handleMonthChange}
                onChange={handleDateChange}
                allowRange={isSelectingRange}
                multiMonth
              />

              <InlineStack align="end" gap="200">
                <Button
                  onClick={() => {
                    setActive(false);
                    setTempDates(value);
                    setIsSelectingRange(false);
                  }}
                >
                  Cancelar
                </Button>
                <Button variant="primary" onClick={handleApply}>
                  Aplicar
                </Button>
              </InlineStack>
            </BlockStack>
          </Box>
        </InlineGrid>
      </Box>
    </Popover>
  );
}

